'use client';
import {
  useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
import { useEffectOnce } from '@utils/react/hooks';
import { getApiKeys } from '@utils/graphql/creators/apiKeys';
import {
  ActionModal,
  Button,
  Modal,
  Table,
} from '@skyline-platform/alchemy';
import { tabEventEmitter } from '../utils/TabEventEmitters';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { parseSSoTEvent, SSOT_ACTIONS } from '@utils/eventHandler';
import '../page.css';
import { SearchBarComponent } from '@components/SearchBar/SearchBarComponent';
import { ApiKeyForm } from '@components/FormComponents/ApiKeyForm/ApiKeyForm';
import {
  buildSearchCriteria,
  normalizeStringFields,
} from '../../utils/helpers';
import { getLogger } from '@utils/general';
import {
  patchRow, removeRow, addRow, renderBadgeForPermission,
} from '../utils/helpers';
import { ApiKeyActions } from './ApiKeyActions';
import { useServerPagination } from '@utils/react/hooks/useServerPagination';
import { SkipToMainContent } from '@components/Misc';

const logger = getLogger('src.pages.apikeys.AllApiKeys');

const API_KEY_FETCH_FIELDS = [
  'id',
  'user_key',
  'description',
  'can_read_write',
  'can_request_snap',
  'can_use_srs',
  'can_set_token_expiration',
  'can_view_private_metadata',
  'date_created',
  'zone.id',
  'zone.name',
  'contact.id',
  'contact.first_name',
  'contact.last_name',
  'contact.title',
  'contact.company_name',
  'contact.primary_phone',
  'contact.email_address',
];

// Maps frontend search field names to backend GraphQL criteria keys.
const SEARCH_FIELD_MAP = {
  user_key: 'userKey',
  description: 'description',
  contact_first_name: 'contactFirstName',
  contact_last_name: 'contactLastName',
  zone: 'zoneName',
};

// Maps table column field names to backend sort field names.
const SORT_FIELD_MAP = {
  user_key: 'user_key',
  contact_first_name: 'contact_first_name',
  contact_last_name: 'contact_last_name',
  zone: 'zone_name',
};

// Fields rendered directly in the API Keys table (non-nested).
// Events where none of these change (and zone/contact IDs are unchanged) are skipped.
const API_KEY_PATCH_FIELDS = new Set([
  'user_key',
  'description',
  'can_read_write',
  'can_request_snap',
  'can_use_srs',
  'can_set_token_expiration',
  'can_view_private_metadata',
]);

export const AllApiKeys = () => {
  const searchOptions = [
    { key: 'User Key', value: 'user_key' },
    { key: 'Description', value: 'description' },
    { key: 'Contact First Name', value: 'contact_first_name' },
    { key: 'Contact Last Name', value: 'contact_last_name' },
    { key: 'Zone', value: 'zone' },
  ];

  const [selectedFields, setSelectedFields] = useState(searchOptions.map((option) => option.value));
  const [newApiKey, setNewApiKey] = useState(null);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState();

  const mapTableData = useCallback((apiKeys) => {
    return apiKeys.map((apiKey) => {
      const apiKeyNormalized = normalizeStringFields(apiKey, ['description']);
      return {
        ...apiKeyNormalized,
        zone: apiKey?.zone.name,
        contact_first_name: apiKey?.contact?.first_name,
        contact_last_name: apiKey?.contact?.last_name,
        action: {
          ...apiKey,
          contact_first_name: apiKey?.contact?.first_name,
          contact_last_name: apiKey?.contact?.last_name,
          email_address: apiKey?.contact?.email_address,
        },
      };
    });
  }, []);

  const {
    rows,
    totalRows,
    loading,
    currentPage,
    rowsPerPage,
    handleSort,
    sortDirection,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    clearSearch,
    refetchCurrentPage,
    setRows,
    setTotalRows,
  } = useServerPagination({
    fetchFn: getApiKeys,
    mapFn: mapTableData,
    fetchFields: API_KEY_FETCH_FIELDS,
    defaultSort: { field: 'user_key', direction: 'asc' },
    sortFieldMap: SORT_FIELD_MAP,
    enableSort: true,
    onError: (error) => logger.error(error),
  });

  const hasActiveCriteriaRef = useRef(false);

  const handleServerSearch = useCallback((searchTerm, fields) => {
    if (!searchTerm) {
      if (hasActiveCriteriaRef.current) {
        hasActiveCriteriaRef.current = false;
        clearSearch();
      }
      return;
    }
    const criteria = buildSearchCriteria(searchTerm, fields, SEARCH_FIELD_MAP);
    if (Object.keys(criteria).length === 0) return;
    hasActiveCriteriaRef.current = true;
    handleSearch(criteria);
  }, [handleSearch, clearSearch]);

  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const tableContext = {
    rowsRef,
    setRows,
    // removeRow calls setSelectedRowIds/setSelectedId to clean up selection state when a row
    // is deleted. AllApiKeys has no multi-select, so setSelectedRowIds is a no-op. It does
    // have a detail panel, so setSelectedId uses the real setter to close the panel if its
    // row is removed.
    setSelectedRowIds: () => {},
    setSelectedId: setSelectedApiKeyId,
  };

  /***** SUBSCRIPTIONS *****/
  // AllApiKeys uses inline patchRow/removeRow/addRow rather than handleTableEvent because
  // apiKey events only ever affect apiKey rows — there is no fan-out to other model types.
  // handleTableEvent is the right tool when a single event needs to ripple into many rows of
  // a different model (e.g. a zone rename affecting stream rows). That complexity is not needed
  // here, so the simpler inline approach is used instead.
  //
  // API_KEY_PATCH_FIELDS gates Update events to flat scalar fields. Changes to
  // the nested zone or contact trigger a targeted fetch to resolve the updated display names.
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    const handleEvent = async (eventMessage) => {
      const parsed = parseSSoTEvent(eventMessage, 'Apikey');
      if (!parsed) return;
      const { action, data } = parsed;

      if (action === SSOT_ACTIONS.REMOVE) {
        if (data.previous?.id) {
          removeRow(data.previous.id, tableContext);
          setTotalRows((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      if (!data.current?.id) return;

      if (action === SSOT_ACTIONS.CREATE) {
        const { query, execute } = getApiKeys(data.current.id);
        query.addReturnProperties(...API_KEY_FETCH_FIELDS);
        try {
          const { data: apiKeyData } = await execute();
          if (apiKeyData) {
            addRow(apiKeyData, mapTableData, tableContext);
            setTotalRows((prev) => prev + 1);
          }
        } catch (err) {
          logger.error(err);
        }
        return;
      }

      if (action === SSOT_ACTIONS.UPDATE) {
        const { current, previous } = data;
        const rowPatch = {};
        for (const field of API_KEY_PATCH_FIELDS) {
          if (current[field] !== undefined && current[field] !== previous?.[field]) {
            rowPatch[field] = current[field] ?? '';
          }
        }
        const zoneChanged = 'zone_id' in current && current.zone_id !== previous?.zone_id;
        const contactChanged = 'contact_id' in current && current.contact_id !== previous?.contact_id;
        if (Object.keys(rowPatch).length === 0 && !zoneChanged && !contactChanged) return;

        if (!zoneChanged && !contactChanged) {
          patchRow(current.id, rowPatch, tableContext);
          return;
        }

        // zone or contact changed — fetch the full api key to get updated nested names
        try {
          const { query, execute } = getApiKeys(current.id);
          query.addReturnProperties(...API_KEY_FETCH_FIELDS);
          const { data: fetched } = await execute();
          if (!fetched) return;
          const [mappedRow] = mapTableData([fetched]);
          const { action: actionObj, ...displayFields } = mappedRow;
          patchRow(current.id, { ...displayFields, ...rowPatch }, tableContext, actionObj);
        } catch (err) {
          logger.error(err);
        }
      }
    };
    messageHandler.subscribe(handleEvent);
    return () => {
      messageHandler.unsubscribe(handleEvent);
    };
  });
  /***** END SUBSCRIPTIONS *****/


  useEffect(() => {
    if (newApiKey?.id) {
      refetchCurrentPage();
      tabEventEmitter.emit('openTab', newApiKey.user_key);
      setTimeout(() => setSelectedApiKeyId(newApiKey.id), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newApiKey]);


  const tableColumns = useMemo(() => [
    {
      field: 'user_key',
      headerName: 'User Key',
      type: 'string',
      grow: 1,
      sortable: true,
      width: '300px',
      cell: (row) => (
        <a
          tabIndex={0}
          className={`api-key-name ${row.__selected ? 'selected-tab' : ''}`}
          id={`datacell-${row?.id || Date.now()}`}
          aria-label={`View details for API key ${row?.user_key}`}
          title={row?.user_key}
          onClick={() => {
            tabEventEmitter.emit('openTab', row.action);
            setSelectedApiKeyId(row?.id);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              tabEventEmitter.emit('openTab', row.action);
              setSelectedApiKeyId(row?.id);
              event.preventDefault();
            }
          }}
        >
          {row?.user_key}
        </a>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      type: 'string',
      grow: 1,
      sortable: true,
    },
    {
      field: 'contact_first_name',
      headerName: 'Contact First Name',
      type: 'string',
      grow: 1,
      sortable: true,
    },
    {
      field: 'contact_last_name',
      headerName: 'Contact Last Name',
      type: 'string',
      grow: 1,
      sortable: true,
    },
    {
      field: 'zone',
      headerName: 'Zone',
      type: 'string',
      grow: 1,
      sortable: true,
      width: '105px',
    },
    {
      field: 'can_read_write',
      headerName: 'Online/Offline a Stream',
      cell: (row) => renderBadgeForPermission(row?.can_read_write, 'Online/Offline a Stream'),
      width: '162px',
      sortable: true,
      center: true,
    },
    {
      field: 'can_request_snap',
      headerName: 'Can Request Snapshot',
      cell: (row) => renderBadgeForPermission(row?.can_request_snap, 'Request Snapshot'),
      width: '148px',
      sortable: true,
      center: true,
    },
    {
      field: 'can_use_srs',
      headerName: 'Can Record',
      cell: (row) => renderBadgeForPermission(row?.can_use_srs, 'Record'),
      width: '108px',
      sortable: true,
      center: true,
    },
    {
      field: 'can_set_token_expiration',
      headerName: 'Can Set Token Expiration',
      cell: (row) => renderBadgeForPermission(row?.can_set_token_expiration, 'Set Token Expiration'),
      width: '162px',
      sortable: true,
      center: true,
    },
    {
      field: 'can_view_private_metadata',
      headerName: 'Can View Secure Data',
      cell: (row) => renderBadgeForPermission(row?.can_view_private_metadata, 'View Secure Data'),
      width: '162px',
      sortable: true,
      center: true,
    },
    {
      field: 'action',
      headerName: '',
      cell: (row) => (
        <ActionModal
          className='api-key-row-actions'
          id={row.user_key}
          title='Toggle Options'
          content={ApiKeyActions({ rowInfo: row.action, id: row.user_key })}
        />
      ),
      width: '50px',
      sortable: false,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [selectedApiKeyId]);


  const displayRows = useMemo(() => {
    return rows.map((item) => ({
      ...item,
      __selected: item.id === selectedApiKeyId,
    }));
  }, [rows, selectedApiKeyId]);

  return (
    <gar-all-apikeys>
      <div className='actions-bar'>
        <SearchBarComponent
          searchOptions={searchOptions}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          fieldName='Api Keys'
          onSearch={handleServerSearch}
        />
        <Modal
          className='new-api-key-modal'
          closeIcon
          header='Create New API Key'
          trigger={({ handleOpen }) => (
            <Button
              label='New Api Key'
              iconLeft='fa-solid fa-circle-plus'
              state='default'
              type='filled'
              onClick={handleOpen}
            />
          )}
          slots={{
            content: (handleClose) => (
              <ApiKeyForm
                handleClose={handleClose}
                setNewCreatedApiKey={setNewApiKey}
              />
            ),
          }}
        />
      </div>
      <SkipToMainContent
        text="Skip to pagination controls"
        targetSelector="alc-pagination-bar"
      />
      <div>
        <Table
          tableName='Api Keys'
          // prevents additional focus on the table itself in firefox
          tabIndex={-1}
          columns={tableColumns}
          getRowId={(row) => row.id}
          hideInColumnMenu={['action']}
          rows={displayRows}
          style={{ height: displayRows?.length === 0 ? '400px' : undefined }}
          initialSortField='user_key'
          defaultSortAsc={true}
          sortServer
          onSort={handleSort}
          paginationServer
          paginationTotalRows={totalRows}
          currentPage={currentPage}
          paginationPerPage={rowsPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          progressPending={loading}
          sortDirection={sortDirection}
          selectedId={selectedApiKeyId}
        />
      </div>
    </gar-all-apikeys>
  );
};